import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Home from "./Home";

const isHeaderEncodingError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);
  return /failed to read the 'headers' property|failed to execute 'fetch'|iso-8859-1|string contains non iso-8859-1 code point/i.test(
    message
  );
};

const clearPersistedAuthState = () => {
  if (typeof window === "undefined") return;

  Object.keys(localStorage)
    .filter((key) => key.includes("-auth-token"))
    .forEach((key) => localStorage.removeItem(key));
};

const Index = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hasSession, setHasSession] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let authResolved = false;

    // Listen first so we don't miss the initial SIGNED_IN event.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return;
      authResolved = true;
      if (session) {
        setHasSession(true);
        setLoading(false);
      } else {
        setHasSession(false);
        setLoading(false);
        navigate("/auth");
      }
    });

    const checkAuth = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (cancelled) return;
        authResolved = true;
        if (session) {
          setHasSession(true);
        } else {
          navigate("/auth");
        }
      } catch (error) {
        console.error("Session bootstrap failed:", error);
        if (isHeaderEncodingError(error)) {
          clearPersistedAuthState();
        }
        // Don't redirect on transient network errors — wait for the auth
        // listener (or the fallback timeout below) to settle.
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    checkAuth();

    // Safety net: if neither getSession nor the listener resolved within
    // 4s, assume signed out and send the user to /auth.
    const fallback = window.setTimeout(() => {
      if (cancelled || authResolved) return;
      console.warn("Auth bootstrap timed out; redirecting to /auth");
      setLoading(false);
      navigate("/auth");
    }, 4000);

    return () => {
      cancelled = true;
      window.clearTimeout(fallback);
      subscription.unsubscribe();
    };
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  // Avoid mounting Home (and firing its data queries) when unauthenticated;
  // the redirect to /auth is in flight.
  if (!hasSession) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <p className="text-muted-foreground">Redirecting...</p>
      </div>
    );
  }

  return <Home />;
};

export default Index;
